<template lang="pug">
div

  section.u-mt-md
    AppTitle.u-mb-xs(:title="$t('mantra')")
    Query(
      :query="require('~/graphql/Work.gql').Works"
      :initialValue="works"
      :variables="({ first, after }) => ({ first, after })"
      v-slot="{ items }"
    )
      AsymetricGrid(:items="items" ref="itemsViewer" v-slot="{ item, ratio }")
        AppCard.is-work(:img="[{ srcset: item.thumb, type: 'image/jpg', ratio }]" :ratio="ratio" :title="item.title" :url="item.url ? item.url : `/work/${item.slug}`")

  section.u-mt-md
    AppLink(to="/ordering" :title="$t('cta')" class="button-link is-large " v-html="$t('cta')")


  Overlay(:is-open.sync="filterOpen")
    .grid.is-widescreen-8.is-desktop-10.u-mh-md
      .column
        h3 Services
        label.checkbox.is-inline(v-for="service in services")
          input.checkbox-input(type="checkbox" v-model="selectedServices" :value="service")
          | {{ service }}
        h3.u-mt-sm Industries
        label.checkbox.is-inline(v-for="industry in industries")
          input.checkbox-input(type="checkbox" v-model="selectedIndustries" :value="industry")
          | {{ industry }}
        h3.u-mt-sm Mood
        .checkboxImages
          label.checkboxImages-item(v-for="mood in 10")
            input.checkboxImages-item-input(type="checkbox")
            img(:src="`https://picsum.photos/800/1000/?image=${Math.floor(Math.random() * 80 + 1)}`")
            .checkboxImages-item-icon: AppIcon(name="check")
        button.button-link.u-mt-sm(@click="applyFilters") Filter


  .u-mb-mi
</template>

<style lang="stylus">
.checkboxImages
  display: flex
  flex-wrap: wrap
  margin-left: -5px
  margin-right: -5px

  &-item
    width: 160px
    height: 160px
    position: relative
    overflow: hidden
    display: block
    margin: 5px
    cursor: pointer

    &-input
      position: absolute
      top: 0
      right: 0
      bottom: 0
      left: 0
      opacity: 0
      z-index: 3

      img
        object-fit: cover
        object-position: center
        width: 100%
        height: 100%

      &:checked
        ~.checkboxImages-item-icon
          opacity: 1 !important

    &-icon
      position: absolute
      top: 50%
      left: 50%
      transform: translate(-50%, -50%)
      z-index: 2
      background: $clWhite
      border-radius: 20em
      padding: 5px
      transition: 200ms
      opacity: 0
</style>

<script>
import AsymetricGrid from '~/components/PsudoRandomGrid';
import { Works } from '~/graphql/Work.gql';
import { load } from '~/directives';
import Query from '~/components/Query';
import Overlay from '~/components/Overlay';

export default {
  scrollToTop: true,
  meta: {
    logoText: 'work'
  },
  head() {
    const $t = this.$t.bind(this);
    return {
      title: $t('pageTitle'),
      meta: [
        {
          hid: 'og:title',
          name: 'og:title',
          property: 'og:title',
          content: $t('pageTitle')
        },
        {
          hid: 'description',
          name: 'description',
          content: $t('pageDesc')
        },
        {
          hid: 'og:description',
          name: 'og:description',
          property: 'og:description',
          content: $t('pageDesc')
        },
        {
          hid: 'og:image',
          name: 'og:image',
          property: 'og:image',
          content: 'https://cdn.baianat.com/static/share/works.jpg'
        },
        {
          hid: 'twitter:image',
          name: 'twitter:image',
          property: 'twitter:image',
          content: 'https://cdn.baianat.com/static/share/works.jpg'
        }
      ]
    };
  },
  components: {
    AsymetricGrid,
    Overlay,
    Query
  },
  directives: { load },
  async asyncData({ app }) {
    const { data } = await app.$api.query({
      query: Works,
      variables: {
        first: 10
      }
    });

    return {
      works: data.works
    };
  },
  data: () => ({
    filterOpen: false,
    selectedIndustries: [],
    selectedServices: [],
    filters: {
      services: [],
      industries: []
    },
    services: [
      'Technology',
      'Typography',
      'Business',
      'Branding',
      'Interaction',
      'Product design',
      'Animation',
      'Advertising',
      'DevOps',
      'Development '
    ],
    industries: [
      'Agriculture',
      'Forestry',
      'Fishing',
      'Mining and Oil',
      'Utilities',
      'Construction',
      'Food and beverage',
      'Textile',
      'Leather products',
      'Wood',
      'Paper production',
      'Printing',
      'Chemical',
      'Plastic and rubber',
      'Electronics',
      'Electrical',
      'Transportation',
      'Furniture',
      'Health care',
      'Medical ',
      'Jewelry',
      'Dolls and Toys',
      'Sports ',
      'Gaming',
      'Ecommerce',
      'Retail',
      'Hosting',
      'Telecommunications',
      'Finance and Banks',
      'Insurance',
      'Real Estate',
      'Professional Services',
      'Education',
      'Restaurants',
      'Hotels',
      'Repair and Maintenance',
      'Organizations',
      'Government',
      'Travel',
      'Gyms',
      'Security',
      'Entertainment',
      'Charity',
      'Call Centers'
    ]
  }),
  methods: {
    async applyFilters() {
      this.works.items = [];
      this.$refs.itemsViewer.set([]);
      this.filters.services = this.selectedServices.map(s => s.toUpperCase().replace(/\s+/g, '_'));
      this.filters.industries = this.selectedIndustries.map(i => i.toUpperCase().replace(/\s+/g, '_'));

      await this.fetchMore();

      this.filterOpen = false;
    }
  }
};
</script>

<i18n>
{
  "en": {
    "pageTitle":"Baianat Work",
    "pageDesc":"See our work in web design, business, development, type design, animation, advertising, product design and technology.",
    "mantra":"Our work and |success partners.",
    "cta":"Let’s have <br> coffee together."
  },
  "ar": {
    "pageTitle":"أعمال بيانات",
    "pageDesc":"اكتشف أعمالنا في تصميم المواقع والتطبيقات والأعمال التجارية والبرمجة والتكنولوجيا وتصميم المنتجات والأنيميشن و الإعلانات",
    "mantra":" أعمالنا و شركائنا |في النجاح.",
    "cta":"دعنا نتناول  <br>القهوة سويًا."
  }
}
</i18n>
